import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, first, switchMap } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { UserProfileService } from '../services/user-profile.service';
import { LOGIN_PATH } from '../../../../apps/values/path';
import {
  THERAPY_MATCH_D_USER_CODE,
  USUAL_CARE_USER_CODE,
} from '../../../../apps/values/therapist-categories';
import { NotificationService } from '@progress/kendo-angular-notification';

@Injectable({
  providedIn: 'root',
})
export class IsUserWithTherapistPrivilegesGuard implements CanActivate {
  constructor(
    private _authService: AuthService,
    private _userProfileService: UserProfileService,
    private _router: Router,
    private _notificationService: NotificationService
  ) {}

  canActivate(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    routerStateSnapshot: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // Get Auth0 user and check their profile in database
    return this._authService.user$.pipe(
      first(),
      switchMap((user) => {
        if (!user?.sub) {
          // No Auth0 user - redirect to login
          void this._router.navigate(['/', LOGIN_PATH]);
          return of(false);
        }

        // Get user profile from database
        return this._userProfileService.getUserProfile(user.sub).pipe(
          map((userProfile) => {
            if (!userProfile) {
              // No profile found - logout
              this._authService.logout({
                logoutParams: {
                  returnTo: window.location.origin + `/${LOGIN_PATH}`,
                },
              });
              return false;
            }

            // Check if user has therapist privileges (has user_group_code)
            if (
              userProfile.user_group_code === THERAPY_MATCH_D_USER_CODE ||
              userProfile.user_group_code === USUAL_CARE_USER_CODE
            ) {
              // Has therapist user_group_code - allow access
              return true;
            } else {
              // No therapist code - deny access
              this._notificationService.show({
                content:
                  "You do not have the correct permissions/privileges to access this application. Please contact your organisation's administrator for further information",
                hideAfter: 3000,
                animation: { type: 'slide', duration: 300 },
                position: { horizontal: 'center', vertical: 'top' },
                type: { style: 'error', icon: true },
              });

              // Logout from Auth0
              this._authService.logout({
                logoutParams: {
                  returnTo: window.location.origin + `/${LOGIN_PATH}`,
                },
              });

              return false;
            }
          })
        );
      })
    );
  }
}
